import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import {
  X, Package, TrendingDown, Award, DollarSign, Loader2, ExternalLink, Target, Check, Pencil,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#FFC40C', '#16a34a', '#3b82f6', '#dc2626', '#a855f7', '#0891b2', '#ea580c', '#0ea5e9'];

const fmtCurrency = (n) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0);
const fmtUnit = (n) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

/**
 * Reusable product detail modal: header, price history line chart, vendor
 * comparison table, per-project usage. Full-screen on mobile.
 */
const ProductDetailModal = ({ productId, open, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editingTarget, setEditingTarget] = useState(false);
  const [targetInput, setTargetInput] = useState('');
  const [savingTarget, setSavingTarget] = useState(false);
  const [chartReady, setChartReady] = useState(false);

  const fetchAnalytics = React.useCallback(() => {
    if (!productId) return;
    setLoading(true);
    axios
      .get(`${API}/products/${productId}/analytics`, { withCredentials: true })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [productId]);

  useEffect(() => {
    if (!open || !productId) return;
    setData(null);
    setEditingTarget(false);
    setTargetInput('');
    setChartReady(false);
    fetchAnalytics();
    // Defer chart mount until the modal has fully painted to avoid the
    // recharts "container width=-1" warning. Use requestAnimationFrame + timeout
    // to ensure the DOM has painted before measuring.
    let cancelled = false;
    const scheduleChartReady = () => {
      // Double RAF ensures the browser has painted the modal
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (cancelled) return;
          setTimeout(() => {
            if (!cancelled) setChartReady(true);
          }, 200);
        });
      });
    };
    scheduleChartReady();
    return () => { cancelled = true; };
  }, [productId, open, fetchAnalytics]);

  const saveTarget = async (clear = false) => {
    setSavingTarget(true);
    try {
      const target_price = clear ? null : parseFloat(targetInput);
      if (!clear && (Number.isNaN(target_price) || target_price < 0)) {
        toast.error('Enter a valid target price');
        setSavingTarget(false);
        return;
      }
      await axios.put(
        `${API}/products/${productId}/target`,
        { target_price },
        { withCredentials: true },
      );
      toast.success(clear ? 'Target price cleared' : 'Target price saved');
      setEditingTarget(false);
      fetchAnalytics();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save target price');
    } finally {
      setSavingTarget(false);
    }
  };

  if (!open) return null;

  // Build per-vendor chart series (recharts line per vendor)
  const trendByVendor = (() => {
    if (!data?.price_history?.length) return { months: [], vendors: [] };
    const vendorsSet = new Set(data.price_history.map((p) => p.vendor_name));
    const vendors = Array.from(vendorsSet);

    const monthMap = {};
    data.price_history.forEach((p) => {
      if (!p.order_date) return;
      const key = p.order_date.slice(0, 7); // YYYY-MM
      monthMap[key] ||= { month: key };
      // Last price wins within the month per vendor
      monthMap[key][p.vendor_name] = p.unit_price;
    });
    const months = Object.values(monthMap).sort((a, b) => a.month.localeCompare(b.month));
    return { months, vendors };
  })();

  return (
    <div
      className="fixed inset-0 z-50 flex items-stretch sm:items-center justify-center bg-black/60"
      data-testid="product-detail-modal"
      onClick={onClose}
    >
      <div
        className="bg-white w-full h-full sm:h-auto sm:max-w-4xl sm:max-h-[90vh] overflow-hidden border-0 sm:border-2 sm:border-black sm:shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-primary border-b-2 border-black px-4 sm:px-6 py-4 flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-10 h-10 bg-black flex items-center justify-center flex-shrink-0">
              <Package className="w-5 h-5 text-primary" />
            </div>
            <div className="min-w-0">
              <h2 className="font-heading font-bold text-xl uppercase tracking-wide truncate">
                {data?.product_name || (loading ? 'Loading…' : 'Product')}
              </h2>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                {data?.category && (
                  <span className="text-[10px] font-heading font-bold uppercase tracking-wider px-1.5 py-0.5 bg-black text-primary">
                    {data.category}
                  </span>
                )}
                {data?.manufacturer && (
                  <span className="text-xs text-zinc-700 font-mono">{data.manufacturer}</span>
                )}
                {data?.manufacturer_sku && (
                  <span className="text-xs text-zinc-700 font-mono">SKU: {data.manufacturer_sku}</span>
                )}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="product-detail-close"
            className="h-10 w-10 flex-shrink-0 bg-white border-2 border-black flex items-center justify-center hover:bg-zinc-100"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-6 bg-zinc-50">
          {loading && (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-zinc-400" />
            </div>
          )}

          {!loading && data && data.data_points === 0 && (
            <div className="bg-white border border-zinc-200 p-10 text-center" data-testid="product-detail-empty">
              <Package className="w-12 h-12 text-zinc-300 mx-auto mb-3" />
              <p className="font-bold text-zinc-700">No order history yet</p>
              <p className="text-sm text-zinc-500 mt-1">
                Once you place orders linked to this catalog product, pricing analytics will appear here.
              </p>
            </div>
          )}

          {!loading && data && data.data_points > 0 && (
            <>
              {/* KPI strip */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-white border-2 border-black p-3" data-testid="kpi-avg-price">
                  <p className="text-[10px] font-heading font-bold uppercase tracking-wider text-zinc-500">Your Avg Price</p>
                  <p className="font-mono text-lg font-bold mt-1">{fmtCurrency(data.avg_unit_price)}<span className="text-zinc-400 text-xs">/{data.unit_of_measure || 'unit'}</span></p>
                </div>
                <div className="bg-white border-2 border-black p-3" data-testid="kpi-total-spent">
                  <p className="text-[10px] font-heading font-bold uppercase tracking-wider text-zinc-500">Total Spent</p>
                  <p className="font-mono text-lg font-bold mt-1">{fmtCurrency(data.total_spent)}</p>
                </div>
                <div className="bg-white border-2 border-black p-3" data-testid="kpi-quantity">
                  <p className="text-[10px] font-heading font-bold uppercase tracking-wider text-zinc-500">Quantity Ordered</p>
                  <p className="font-mono text-lg font-bold mt-1">{fmtUnit(data.total_quantity)}</p>
                </div>
                <div className="bg-white border-2 border-black p-3" data-testid="kpi-est-annual">
                  <p className="text-[10px] font-heading font-bold uppercase tracking-wider text-zinc-500">Est. Annual Spend</p>
                  <p className="font-mono text-lg font-bold mt-1">{fmtCurrency(data.estimated_annual_spend)}</p>
                </div>
              </div>

              {/* Target Price panel */}
              <div
                className={`border-2 ${data.above_target ? 'border-red-500 bg-red-50' : 'border-black bg-white'} p-4 flex items-center justify-between gap-3 flex-wrap`}
                data-testid="target-price-panel"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`w-10 h-10 flex items-center justify-center flex-shrink-0 ${data.above_target ? 'bg-red-500 text-white' : 'bg-black text-primary'}`}>
                    <Target className="w-5 h-5" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] font-heading font-bold uppercase tracking-[0.15em] text-zinc-500">Target Price</p>
                    {editingTarget ? (
                      <div className="flex items-center gap-2 mt-1">
                        <span className="font-mono text-lg">$</span>
                        <input
                          type="number"
                          step="0.0001"
                          min="0"
                          autoFocus
                          value={targetInput}
                          onChange={(e) => setTargetInput(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') saveTarget(false); }}
                          data-testid="target-price-input"
                          className="w-32 h-10 border-2 border-black px-2 font-mono text-base focus:outline-none focus:bg-primary/20"
                        />
                        <span className="text-xs text-zinc-500">/ {data.unit_of_measure || 'unit'}</span>
                      </div>
                    ) : data.target_price != null ? (
                      <p className="font-mono text-2xl font-black mt-0.5" data-testid="target-price-value">
                        {fmtCurrency(data.target_price)}
                        <span className="text-xs text-zinc-500 font-mono font-normal ml-1">/ {data.unit_of_measure || 'unit'}</span>
                      </p>
                    ) : (
                      <p className="text-sm text-zinc-500 mt-0.5">No target set — set one to get alerts when avg paid exceeds it.</p>
                    )}
                    {data.above_target && data.target_price != null && (
                      <p className="text-xs text-red-700 font-medium mt-1" data-testid="target-above-warning">
                        Currently paying <span className="font-bold">{fmtCurrency(data.avg_unit_price)}</span> avg — {(((data.avg_unit_price - data.target_price) / data.target_price) * 100).toFixed(0)}% above target.
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {editingTarget ? (
                    <>
                      <button
                        onClick={() => { setEditingTarget(false); setTargetInput(''); }}
                        className="h-10 px-3 bg-white border-2 border-black text-xs font-heading font-bold uppercase hover:bg-zinc-50"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => saveTarget(false)}
                        disabled={savingTarget}
                        data-testid="target-price-save"
                        className="h-10 px-3 bg-primary border-2 border-black text-xs font-heading font-bold uppercase hover:bg-primary-hover flex items-center gap-1"
                      >
                        {savingTarget ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Check className="w-3.5 h-3.5" /> Save</>}
                      </button>
                    </>
                  ) : (
                    <>
                      {data.target_price != null && (
                        <button
                          onClick={() => saveTarget(true)}
                          disabled={savingTarget}
                          data-testid="target-price-clear"
                          className="h-10 px-3 bg-white border-2 border-black text-xs font-heading font-bold uppercase hover:bg-zinc-50"
                          title="Clear target"
                        >
                          Clear
                        </button>
                      )}
                      <button
                        onClick={() => {
                          setEditingTarget(true);
                          setTargetInput(data.target_price != null ? String(data.target_price) : '');
                        }}
                        data-testid="target-price-edit"
                        className="h-10 px-3 bg-primary border-2 border-black text-xs font-heading font-bold uppercase tracking-wider hover:bg-primary-hover flex items-center gap-1"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                        {data.target_price != null ? 'Edit' : 'Set Target'}
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* Price History chart */}
              {trendByVendor.months.length > 0 && (
                <div className="bg-white border border-zinc-200">
                  <div className="px-4 py-3 border-b border-zinc-200 flex items-center gap-2">
                    <TrendingDown className="w-4 h-4 text-zinc-700" />
                    <h3 className="font-heading font-bold text-sm uppercase tracking-wider">Price History</h3>
                  </div>
                  <div className="p-3 h-72 min-w-[280px]" data-testid="price-history-chart">
                    {chartReady ? (
                      <ResponsiveContainer width="100%" height="100%" minWidth={280} minHeight={260}>
                        <LineChart data={trendByVendor.months}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                          <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                          <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${v}`} />
                          <Tooltip formatter={(v) => fmtCurrency(v)} />
                          <Legend wrapperStyle={{ fontSize: 11 }} />
                          {trendByVendor.vendors.map((v, i) => (
                            <Line
                              key={v}
                              type="monotone"
                              dataKey={v}
                              stroke={COLORS[i % COLORS.length]}
                              strokeWidth={2}
                              dot={{ r: 3 }}
                              connectNulls
                            />
                          ))}
                        </LineChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="h-full flex items-center justify-center">
                        <Loader2 className="w-5 h-5 animate-spin text-zinc-300" />
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Vendor Comparison */}
              <div className="bg-white border border-zinc-200">
                <div className="px-4 py-3 border-b border-zinc-200 flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2">
                    <Award className="w-4 h-4 text-zinc-700" />
                    <h3 className="font-heading font-bold text-sm uppercase tracking-wider">Vendor Comparison</h3>
                  </div>
                  {data.best_price_vendor && (
                    <div className="text-xs flex items-center gap-2">
                      <span className="text-zinc-500">Best price:</span>
                      <span className="font-bold text-emerald-700">{data.best_price_vendor.vendor_name}</span>
                      <span className="font-mono text-emerald-700">{fmtCurrency(data.best_price_vendor.avg_price)}</span>
                    </div>
                  )}
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[640px] text-sm" data-testid="vendor-comparison-table">
                    <thead>
                      <tr className="bg-zinc-50 border-b border-zinc-200">
                        <th className="text-left px-3 py-2 text-[10px] font-heading font-bold uppercase tracking-wider text-zinc-500">Vendor</th>
                        <th className="text-right px-3 py-2 text-[10px] font-heading font-bold uppercase tracking-wider text-zinc-500">Avg Price</th>
                        <th className="text-right px-3 py-2 text-[10px] font-heading font-bold uppercase tracking-wider text-zinc-500">Min / Max</th>
                        <th className="text-right px-3 py-2 text-[10px] font-heading font-bold uppercase tracking-wider text-zinc-500">Orders</th>
                        <th className="text-right px-3 py-2 text-[10px] font-heading font-bold uppercase tracking-wider text-zinc-500">Qty</th>
                        <th className="text-right px-3 py-2 text-[10px] font-heading font-bold uppercase tracking-wider text-zinc-500">Last Order</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.vendor_comparison.map((v) => (
                        <tr
                          key={v.vendor_name}
                          className={`border-b border-zinc-100 ${v.vendor_name === data.best_price_vendor?.vendor_name ? 'bg-emerald-50' : ''}`}
                          data-testid={`vendor-row-${v.vendor_name}`}
                        >
                          <td className="px-3 py-2 font-medium">
                            {v.vendor_name === data.best_price_vendor?.vendor_name && (
                              <Award className="inline w-3 h-3 mr-1 text-emerald-600" />
                            )}
                            {v.vendor_name}
                          </td>
                          <td className="px-3 py-2 text-right font-mono font-bold">{fmtCurrency(v.avg_price)}</td>
                          <td className="px-3 py-2 text-right font-mono text-xs text-zinc-500">{fmtCurrency(v.min_price)} – {fmtCurrency(v.max_price)}</td>
                          <td className="px-3 py-2 text-right font-mono">{v.order_count}</td>
                          <td className="px-3 py-2 text-right font-mono">{fmtUnit(v.total_quantity)}</td>
                          <td className="px-3 py-2 text-right text-xs text-zinc-500">{v.last_order_date?.slice(0, 10) || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Project Usage */}
              {data.project_usage?.length > 0 && (
                <div className="bg-white border border-zinc-200">
                  <div className="px-4 py-3 border-b border-zinc-200 flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-zinc-700" />
                    <h3 className="font-heading font-bold text-sm uppercase tracking-wider">Usage by Project</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[400px] text-sm" data-testid="project-usage-table">
                      <thead>
                        <tr className="bg-zinc-50 border-b border-zinc-200">
                          <th className="text-left px-3 py-2 text-[10px] font-heading font-bold uppercase tracking-wider text-zinc-500">Project</th>
                          <th className="text-right px-3 py-2 text-[10px] font-heading font-bold uppercase tracking-wider text-zinc-500">Quantity</th>
                          <th className="text-right px-3 py-2 text-[10px] font-heading font-bold uppercase tracking-wider text-zinc-500">Spent</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.project_usage.map((p, i) => (
                          <tr key={p.project_name + i} className="border-b border-zinc-100">
                            <td className="px-3 py-2">{p.project_name}</td>
                            <td className="px-3 py-2 text-right font-mono">{fmtUnit(p.quantity)}</td>
                            <td className="px-3 py-2 text-right font-mono font-bold">{fmtCurrency(p.spent)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductDetailModal;
export { ExternalLink };
